import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AI_MODELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkle, FloppyDisk } from "@phosphor-icons/react";

export default function Reviews() {
  return (
    <div data-testid="reviews-page">
      <Tabs defaultValue="weekly">
        <TabsList className="mb-4">
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly Review</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly Review</TabsTrigger>
        </TabsList>
        <TabsContent value="weekly"><ReviewEditor period="weekly" /></TabsContent>
        <TabsContent value="monthly"><ReviewEditor period="monthly" /></TabsContent>
      </Tabs>
    </div>
  );
}

function ReviewEditor({ period }) {
  const [model, setModel] = useState("gpt-5.4");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const draft = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/review", { period, model });
      setText(res.data.draft);
      toast.success("AI draft generated — review and edit as needed");
    } catch {
      toast.error("Could not generate draft");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      await api.post("/data/reviews", { period, content: text, date: new Date().toISOString().slice(0, 10) });
      toast.success("Review saved");
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold text-navy capitalize">{period} Review — AI Draft</h3>
          <p className="text-sm text-slate">Auto-drafted from this period's live data. Team reviews and edits rather than compiling from scratch.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid={`${period}-model`} className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{AI_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid={`${period}-generate`} onClick={draft} disabled={loading} className="bg-gold hover:bg-gold/90 text-navy gap-1.5">
            <Sparkle size={16} weight="fill" />{loading ? "Drafting…" : "Generate Draft"}
          </Button>
        </div>
      </div>
      <Textarea
        data-testid={`${period}-content`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Click "Generate Draft" to auto-create your ${period} review from live data, then edit here…`}
        rows={18}
        className="font-mono text-sm focus-visible:ring-gold"
      />
      <div className="flex justify-end">
        <Button data-testid={`${period}-save`} onClick={save} disabled={!text} variant="outline" className="gap-1.5">
          <FloppyDisk size={16} /> Save Review
        </Button>
      </div>
    </div>
  );
}
